// Admin → Clinical → Reference Library (G1; docs/HMS_V4_CLINICAL_RAG_DESIGN.md §23).
// The hospital's approved clinical references. A document is added (file or
// pasted text), read into passages, submitted, and approved by a second person
// before anyone's question can be answered from it. Tabs are URL levels
// (ADMIN_SUBTABS): Documents, Search settings, Web search (Web Search
// References — external trusted sites, contract §6ai; WebSearchSettings.tsx).
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ExternalLink, Plus, RefreshCw } from 'lucide-react';
import AdminTabs from '../AdminTabs';
import { useAdminSubTab } from '../adminRoute';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { FormField } from '@/components/ui/FormField';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { RowActionsMenu, type RowAction } from '@/components/ui/RowActionsMenu';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { ErrorState, LoadingState } from '@/components/ui/States';
import { TextPromptDialog } from '@/components/ui/TextPromptDialog';
import { toast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { keys, tenantKey } from '@/lib/queryKeys';
import { SettingsToolbar, matchesSearch } from '../shared/SettingsToolbar';
import { getSpecialties } from '../api';
import { WebSearchSettingsView } from './WebSearchSettings';
import {
  CATEGORIES, CATEGORY_LABEL, addLibraryDoc, approveLibraryDoc, deleteLibraryDoc, fetchLibrary, fetchLibraryDoc, fetchLibraryStatus,
  openLibraryFile, reindexAll, reindexLibraryDoc, rejectLibraryDoc, retireLibraryDoc, saveLibrarySettings, submitLibraryDoc,
  type Category, type DocStatus, type LibraryDoc, type LibraryStatus,
} from '@/features/clinical-references/api';

const STATUS: Record<DocStatus, { label: string; tone: 'success' | 'warn' | 'neutral' | 'info' | 'danger' }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  pending_approval: { label: 'Awaiting approval', tone: 'warn' },
  approved: { label: 'Approved', tone: 'success' },
  rejected: { label: 'Sent back', tone: 'danger' },
  retired: { label: 'Retired', tone: 'neutral' },
};

function readingBadge(d: LibraryDoc) {
  if (d.index_status === 'processing' || d.index_status === 'pending') return <Badge variant="info">Reading…</Badge>;
  if (d.index_status === 'failed') return <Badge variant="danger">Couldn&rsquo;t read</Badge>;
  if (d.index_error) return <Badge variant="warn">Words only</Badge>;
  return <Badge variant="success">{d.chunk_count} passages</Badge>;
}

const showDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export default function ReferenceLibraryTab() {
  const tabs = useAdminSubTab('clinical-library');
  const status = useQuery({ queryKey: keys.admin.clinicalLibraryStatus(), queryFn: fetchLibraryStatus });
  return (
    <div className="fade-in flex flex-col gap-4">
      <AdminTabs state={tabs} />
      {status.data && <StatusLine s={status.data} />}
      {tabs.active === 'documents' && <DocumentsView status={status.data} />}
      {tabs.active === 'settings' && (status.data ? <SettingsView s={status.data} /> : <LoadingState />)}
      {tabs.active === 'web-search' && <WebSearchSettingsView />}
    </div>
  );
}

function StatusLine({ s }: { s: LibraryStatus }) {
  const qc = useQueryClient();
  const all = useMutation({
    mutationFn: reindexAll,
    onSuccess: (r) => { toast({ title: `Re-indexing ${r.started} document${r.started === 1 ? '' : 's'}`, variant: 'success' }); qc.invalidateQueries({ queryKey: tenantKey('admin', 'clinical-library') }); },
    onError: (e) => toast({ title: 'Couldn’t start re-indexing', description: errText(e), variant: 'danger' }),
  });
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-border bg-card px-3 py-2 text-[12.5px] text-ink-muted">
      <span className="flex items-center gap-1">
        {s.vector_search ? <CheckCircle2 className="h-3.5 w-3.5 text-success" /> : <AlertTriangle className="h-3.5 w-3.5 text-warn-ink" />}
        {s.vector_search ? <>Search by meaning and words · <span className="font-mono">{s.embedding_model}</span></> : 'Search by words only (this database has no vector search)'}
      </span>
      <span>Approved <b className="text-ink">{s.approved}</b></span>
      <span>Awaiting approval <b className="text-ink">{s.pending}</b></span>
      {s.vector_search && s.not_embedded > 0 && s.can_manage && (
        <Button variant="link" className="text-[12.5px] text-warn-ink" loading={all.isPending} onClick={() => all.mutate()}>
          {s.not_embedded} not embedded with the current model — re-index all
        </Button>
      )}
    </div>
  );
}

const FILTERS: { value: string; label: string }[] = [
  { value: 'current', label: 'In use and in progress' },
  { value: 'pending_approval', label: 'Awaiting approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'draft', label: 'Drafts' },
  { value: 'rejected', label: 'Sent back' },
  { value: 'retired', label: 'Retired' },
  { value: '', label: 'All' },
];

function DocumentsView({ status }: { status?: LibraryStatus }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [filter, setFilter] = useState('current');
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState<{ supersedes?: LibraryDoc } | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const list = useQuery({
    queryKey: keys.admin.clinicalLibrary({ filter }),
    queryFn: () => fetchLibrary({ status: filter }),
    // While a document is being read, keep the list current.
    refetchInterval: (q) => ((q.state.data as LibraryDoc[] | undefined)?.some((d) => d.index_status === 'processing' || d.index_status === 'pending') ? 3000 : false),
  });
  const rows = useMemo(() => (list.data ?? []).filter((d) => matchesSearch(search, d.title, d.version_label, d.uploaded_by)), [list.data, search]);
  const refresh = () => qc.invalidateQueries({ queryKey: tenantKey('admin', 'clinical-library') });
  const canManage = !!status?.can_manage;

  const columns: DataTableColumn<LibraryDoc>[] = [
    { key: 'title', header: 'Document', sortable: true, sortValue: (d) => d.title, render: (d) => (
      <span className="flex flex-col">
        <span className="font-semibold text-ink">{d.title}</span>
        <span className="text-[11.5px] text-ink-muted">{[CATEGORY_LABEL[d.category], d.department, d.version_label ? `v${d.version_label}` : null, d.file_name || (d.pasted ? 'pasted text' : null)].filter(Boolean).join(' · ')}</span>
      </span>
    ) },
    { key: 'status', header: 'Status', widthClassName: 'w-[150px]', render: (d) => <Badge variant={STATUS[d.status].tone}>{STATUS[d.status].label}</Badge> },
    { key: 'reading', header: 'Reading', widthClassName: 'w-[130px]', render: readingBadge },
    { key: 'dates', header: 'In effect / review', widthClassName: 'w-[170px]', className: 'text-ink-muted', render: (d) => (
      <span className="flex flex-col text-[12px]">
        <span>{d.effective_from ? `from ${showDate(d.effective_from)}` : 'In effect'}</span>
        {d.review_due && <span className={new Date(d.review_due) < new Date() ? 'font-semibold text-warn-ink' : ''}>review {showDate(d.review_due)}</span>}
      </span>
    ) },
    { key: 'updated', header: 'Updated', widthClassName: 'w-[110px]', sortable: true, sortValue: (d) => d.updated_at, className: 'text-ink-muted', render: (d) => showDate(d.updated_at) },
    { key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', widthClassName: 'w-[56px]', render: (d) => (
      <RowActionsMenu label={`Actions for ${d.title}`} actions={[{ label: 'Open', onSelect: () => setOpenId(d.id) }]} />
    ) },
  ];

  return (
    <div>
      <SettingsToolbar
        search={search}
        onSearch={setSearch}
        placeholder="Search documents"
        count={list.data ? `${rows.length} document${rows.length === 1 ? '' : 's'}` : undefined}
        action={canManage ? <Button onClick={() => setAdding({})}><Plus className="h-4 w-4" /> Add document</Button> : undefined}
      />
      <div className="mb-2 flex items-center gap-2 text-[12px] text-ink-muted">
        Show
        <NativeSelect value={filter} onChange={(e) => setFilter(e.target.value)} className="w-auto text-xs" aria-label="Which documents">
          {FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </NativeSelect>
      </div>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(d) => d.id}
        onRowClick={(d) => setOpenId(d.id)}
        isLoading={list.isLoading}
        isError={list.isError}
        onRetry={() => list.refetch()}
        emptyLabel={search ? 'No documents match' : 'No documents here yet'}
      />
      <AddDocumentSheet
        open={!!adding}
        supersedes={adding?.supersedes}
        onClose={() => setAdding(null)}
        onAdded={(d) => { setAdding(null); refresh(); setOpenId(d.id); toast({ title: 'Added — it is being read now', variant: 'success' }); }}
      />
      <DocumentSheet
        id={openId}
        status={status}
        userId={user?.id ?? null}
        onClose={() => setOpenId(null)}
        onChanged={refresh}
        onNewVersion={(d) => { setOpenId(null); setAdding({ supersedes: d }); }}
      />
    </div>
  );
}

function AddDocumentSheet({ open, supersedes, onClose, onAdded }: { open: boolean; supersedes?: LibraryDoc; onClose: () => void; onAdded: (d: LibraryDoc) => void }) {
  const [mode, setMode] = useState<'file' | 'text'>('file');
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<Category>('protocol');
  const [version, setVersion] = useState('');
  const [description, setDescription] = useState('');
  const [effective, setEffective] = useState('');
  const [review, setReview] = useState('');
  const [department, setDepartment] = useState('');
  const specialties = useQuery({ queryKey: keys.config.specialties(), queryFn: () => getSpecialties(), enabled: open });
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode('file'); setFile(null); setText(''); setTouched(false); setError(''); setVersion(''); setEffective(''); setReview('');
    setTitle(supersedes?.title ?? ''); setCategory(supersedes?.category ?? 'protocol'); setDescription(supersedes?.description ?? '');
    setDepartment(supersedes?.specialty_id ? String(supersedes.specialty_id) : '');
  }, [open, supersedes]);

  const missing = !title.trim() || (mode === 'file' ? !file : text.trim().length < 50);
  const save = async () => {
    setTouched(true);
    if (missing) return;
    setSaving(true); setError('');
    try {
      const fields = { title: title.trim(), category, version_label: version.trim() || undefined, description: description.trim() || undefined,
        effective_from: effective || undefined, review_due: review || undefined, supersedes_id: supersedes?.id ?? null,
        specialty_id: department ? Number(department) : null };
      onAdded(await addLibraryDoc(fields, mode === 'file' ? { file: file! } : { text }));
    } catch (e) { setError(errText(e)); }
    setSaving(false);
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="sm:max-w-[520px]">
        <SheetHeader>
          <SheetTitle>{supersedes ? 'Upload a new version' : 'Add a reference document'}</SheetTitle>
          <SheetDescription>
            {supersedes
              ? `Replaces "${supersedes.title}"${supersedes.version_label ? ` v${supersedes.version_label}` : ''} once the new version is approved.`
              : 'It is read into passages, then needs approval by a second person before anyone can be answered from it.'}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          {error && <div className="rounded-md bg-danger-soft px-3 py-2 text-[12.5px] text-danger-ink">{error}</div>}
          <div className="flex gap-2" role="radiogroup" aria-label="Source">
            {(['file', 'text'] as const).map((m) => (
              <Button key={m} size="sm" variant={mode === m ? 'primary' : 'ghost'} aria-pressed={mode === m} onClick={() => setMode(m)}>
                {m === 'file' ? 'Upload a file' : 'Paste text'}
              </Button>
            ))}
          </div>
          {mode === 'file' ? (
            <FormField label="File" required hint="PDF with a text layer, Word (.docx), text or Markdown, or a PNG/JPG/TIFF scan. Up to 25 MB."
              error={touched && !file ? 'Choose a file' : undefined}>
              <Input type="file" accept=".pdf,.docx,.txt,.md,.png,.jpg,.jpeg,.tif,.tiff" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </FormField>
          ) : (
            <FormField label="Text" required hint="Use lines starting with # for section headings, so citations name the section."
              error={touched && text.trim().length < 50 ? 'Paste at least a paragraph' : undefined}>
              <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} />
            </FormField>
          )}
          <FormField label="Title" required error={touched && !title.trim() ? 'Enter a title' : undefined}>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Antibiotic policy" />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Category">
              <NativeSelect value={category} onChange={(e) => setCategory(e.target.value as Category)}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Department" hint="Optional — the department it belongs to">
              <NativeSelect value={department} onChange={(e) => setDepartment(e.target.value)}>
                <option value="">All departments</option>
                {specialties.data?.map((sp) => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Version" hint="As printed on the document">
              <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="e.g. 2026 rev 2" maxLength={40} />
            </FormField>
            <FormField label="Takes effect" hint="Not searched before this date">
              <Input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} />
            </FormField>
            <FormField label="Review due">
              <Input type="date" value={review} onChange={(e) => setReview(e.target.value)} />
            </FormField>
          </div>
          <FormField label="Notes" hint="Optional: what it covers, who owns it">
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </FormField>
        </SheetBody>
        <SheetFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={saving}>{supersedes ? 'Upload new version' : 'Add document'}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function DocumentSheet({ id, status, userId, onClose, onChanged, onNewVersion }: {
  id: number | null; status?: LibraryStatus; userId: number | null;
  onClose: () => void; onChanged: () => void; onNewVersion: (d: LibraryDoc) => void;
}) {
  const [prompt, setPrompt] = useState<'reject' | 'retire' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const doc = useQuery({
    queryKey: keys.admin.clinicalLibraryDoc(id ?? 0),
    queryFn: () => fetchLibraryDoc(id!),
    enabled: id != null,
    refetchInterval: (q) => (['processing', 'pending'].includes((q.state.data as LibraryDoc | undefined)?.index_status ?? '') ? 2000 : false),
  });
  const run = useMutation({
    mutationFn: async ({ fn, ok }: { fn: () => Promise<unknown>; ok: string }) => { await fn(); return ok; },
    onSuccess: (ok) => { toast({ title: ok, variant: 'success' }); setPrompt(null); doc.refetch(); onChanged(); },
    onError: (e) => toast({ title: 'Not done', description: errText(e), variant: 'danger' }),
  });
  const d = doc.data;
  const canManage = !!status?.can_manage;
  const canApprove = !!status?.can_approve;
  const ownUpload = !!d && d.uploaded_by_user_id === userId;
  const ready = d?.index_status === 'ready';

  const actions: RowAction[] = [];
  if (d) {
    if (canManage && ['draft', 'rejected'].includes(d.status)) actions.push({ label: 'Submit for approval', disabled: !ready, hint: ready ? undefined : 'Wait until it has been read', onSelect: () => run.mutate({ fn: () => submitLibraryDoc(d.id), ok: 'Submitted for approval' }) });
    if (canApprove && d.status === 'pending_approval') {
      actions.push({ label: 'Approve', disabled: ownUpload || !ready, hint: ownUpload ? 'Someone else must approve a document you added' : !ready ? 'It is being read again' : undefined,
        onSelect: () => run.mutate({ fn: () => approveLibraryDoc(d.id), ok: 'Approved — doctors can now be answered from it' }) });
      actions.push({ label: 'Send back…', onSelect: () => setPrompt('reject') });
    }
    if (canManage && d.status === 'approved') actions.push({ label: 'Upload new version…', onSelect: () => onNewVersion(d) });
    if (canManage && d.status !== 'retired') actions.push({ label: 'Read again (re-index)', disabled: d.index_status === 'processing', onSelect: () => run.mutate({ fn: () => reindexLibraryDoc(d.id), ok: 'Reading it again' }) });
    if (d.file_name || d.pasted) actions.push({ label: 'Open original', onSelect: () => openLibraryFile(d.id).catch((e) => toast({ title: errText(e), variant: 'danger' })) });
    if (canManage && d.status === 'approved') actions.push({ label: 'Retire…', danger: true, onSelect: () => setPrompt('retire') });
    if (canManage && ['draft', 'rejected'].includes(d.status) && !d.approved_at) actions.push({ label: 'Delete…', danger: true, onSelect: () => setConfirmDelete(true) });
  }

  return (
    <Sheet open={id != null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {d?.title ?? 'Document'}
            {d && <Badge variant={STATUS[d.status].tone}>{STATUS[d.status].label}</Badge>}
          </SheetTitle>
          <SheetDescription>
            {d ? [CATEGORY_LABEL[d.category], d.version_label ? `version ${d.version_label}` : null, `added by ${d.uploaded_by ?? '—'} on ${showDate(d.created_at)}`].filter(Boolean).join(' · ') : ''}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          {doc.isLoading && <LoadingState />}
          {doc.isError && <ErrorState message="Couldn’t load the document." onRetry={() => doc.refetch()} />}
          {d && (
            <div className="flex flex-col gap-4 text-[13px]">
              {d.rejection_reason && d.status === 'rejected' && <div className="rounded-md bg-danger-soft px-3 py-2 text-danger-ink">Sent back: {d.rejection_reason}</div>}
              {d.retire_reason && <div className="rounded-md bg-muted px-3 py-2 text-ink-muted">Retired {showDate(d.retired_at)} by {d.retired_by}: {d.retire_reason}</div>}
              {d.index_status === 'failed' && <div className="rounded-md bg-danger-soft px-3 py-2 text-danger-ink">Couldn&rsquo;t read it: {d.index_error}</div>}
              {d.index_status === 'ready' && d.index_error && <div className="rounded-md bg-warn-soft px-3 py-2 text-warn-ink">{d.index_error}</div>}
              {['pending', 'processing'].includes(d.index_status) && <div className="flex items-center gap-1.5 rounded-md bg-info-soft px-3 py-2 text-info-ink"><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Reading the document…</div>}
              <dl className="grid grid-cols-[130px_1fr] gap-x-3 gap-y-1">
                <dt className="text-ink-muted">Department</dt><dd>{d.department ?? 'All departments'}</dd>
                <dt className="text-ink-muted">Takes effect</dt><dd>{d.effective_from ? showDate(d.effective_from) : 'Immediately when approved'}</dd>
                <dt className="text-ink-muted">Review due</dt><dd>{showDate(d.review_due)}</dd>
                <dt className="text-ink-muted">Approved</dt><dd>{d.approved_at ? `${showDate(d.approved_at)} by ${d.approved_by}` : '—'}</dd>
                <dt className="text-ink-muted">Cited in answers</dt><dd>{d.times_cited}</dd>
                {d.description && <><dt className="text-ink-muted">Notes</dt><dd>{d.description}</dd></>}
              </dl>
              {d.versions.length > 1 && (
                <div>
                  <div className="mb-1 font-semibold text-ink">Versions</div>
                  <ul className="flex flex-col gap-0.5 text-[12.5px]">
                    {d.versions.map((v) => (
                      <li key={v.id} className={v.id === d.id ? 'font-semibold text-ink' : 'text-ink-muted'}>
                        {v.version_label ? `v${v.version_label}` : `#${v.id}`} · {STATUS[v.status].label}{v.approved_at ? ` · approved ${showDate(v.approved_at)}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <div className="mb-1 flex items-center justify-between font-semibold text-ink">
                  How it was read <span className="text-[12px] font-normal text-ink-muted">{d.passages.length} passages{d.embedding_model ? ` · ${d.embedding_model}` : ''}</span>
                </div>
                <p className="mb-2 text-[12px] text-ink-muted">These are the pieces doctors&rsquo; questions are answered and cited from. Check the sections and pages look right before approving.</p>
                <ol className="flex max-h-[360px] flex-col gap-1.5 overflow-auto">
                  {d.passages.map((p) => (
                    <li key={p.ordinal} className="rounded-md border border-border px-2.5 py-1.5">
                      <div className="text-[11.5px] font-medium text-ink-muted">{p.heading}{p.page ? ` · page ${p.page}` : ''}</div>
                      <div className="text-[12.5px] text-ink">{p.preview}{p.char_count > 400 ? '…' : ''}</div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}
        </SheetBody>
        {actions.length > 0 && (
          <SheetFooter className="justify-between">
            <Button variant="ghost" onClick={() => d && openLibraryFile(d.id).catch((e) => toast({ title: errText(e), variant: 'danger' }))}><ExternalLink className="mr-1 h-3.5 w-3.5" /> Original</Button>
            <RowActionsMenu label="Document actions" actions={actions} />
          </SheetFooter>
        )}
        <TextPromptDialog
          open={prompt != null}
          title={prompt === 'reject' ? 'Send this document back?' : 'Retire this document?'}
          description={prompt === 'reject' ? 'The person who added it sees your reason and can correct it.' : 'It stops being searched straight away. Answers given earlier still show what they cited.'}
          label="Reason"
          multiline
          confirmLabel={prompt === 'reject' ? 'Send back' : 'Retire'}
          danger={prompt === 'retire'}
          pending={run.isPending}
          onConfirm={(reason) => d && run.mutate(prompt === 'reject'
            ? { fn: () => rejectLibraryDoc(d.id, reason), ok: 'Sent back' }
            : { fn: () => retireLibraryDoc(d.id, reason), ok: 'Retired' })}
          onClose={() => setPrompt(null)}
        />
        <ConfirmDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          title="Delete this document?"
          description="It was never approved, so no answer used it. The file is deleted too."
          confirmLabel="Delete"
          onConfirm={() => d && run.mutate({ fn: () => deleteLibraryDoc(d.id), ok: 'Deleted' }, { onSuccess: () => onClose() })}
        />
      </SheetContent>
    </Sheet>
  );
}

function SettingsView({ s }: { s: LibraryStatus }) {
  const qc = useQueryClient();
  const [provider, setProvider] = useState(s.settings.embedding_provider);
  const [topK, setTopK] = useState(String(s.settings.top_k));
  const [minSim, setMinSim] = useState(String(s.settings.min_similarity));
  const [keepDays, setKeepDays] = useState(String(s.settings.history_retention_days));
  const save = useMutation({
    mutationFn: () => saveLibrarySettings({ embedding_provider: provider, top_k: Number(topK), min_similarity: Number(minSim), history_retention_days: Number(keepDays) }),
    onSuccess: () => { toast({ title: 'Saved', variant: 'success' }); qc.invalidateQueries({ queryKey: keys.admin.clinicalLibraryStatus() }); },
    onError: (e) => toast({ title: 'Not saved', description: errText(e), variant: 'danger' }),
  });
  const providerChanged = provider !== s.settings.embedding_provider;
  return (
    <div className="flex max-w-[640px] flex-col gap-4 rounded-xl border border-border bg-card p-4 text-[13px]">
      <FormField label="Embedding service" hint="Turns passages into numbers so questions can be matched by meaning. Anthropic has no such service, so a hospital using it for AI still needs an OpenAI or Gemini key here.">
        <NativeSelect value={provider} onChange={(e) => setProvider(e.target.value as 'openai' | 'gemini')} disabled={!s.can_manage}>
          <option value="openai">OpenAI (text-embedding-3-small)</option>
          <option value="gemini">Gemini (gemini-embedding-001)</option>
        </NativeSelect>
      </FormField>
      {providerChanged && <div className="rounded-md bg-warn-soft px-3 py-2 text-[12.5px] text-warn-ink">After changing it, use “re-index all” on the status line: passages embedded by the old service are not compared with questions embedded by the new one.</div>}
      <FormField label="Passages per answer" hint="How many of the best-matching passages the AI may answer from (2–12). More gives fuller answers and costs more.">
        <Input type="number" min={2} max={12} value={topK} onChange={(e) => setTopK(e.target.value)} disabled={!s.can_manage} />
      </FormField>
      <FormField label="Minimum similarity" hint="How close in meaning a passage must be to count as evidence (0.05–0.95). Higher refuses more questions as “not covered”; lower risks answering from loosely related text. Start at 0.30 and tune with real questions.">
        <Input type="number" step={0.05} min={0.05} max={0.95} value={minSim} onChange={(e) => setMinSim(e.target.value)} disabled={!s.can_manage} />
      </FormField>
      <FormField label="Keep question history (days)" hint="Doctors' questions, the patient summary sent with them and the passages cited are deleted after this many days (0 keeps them). The documents and their approval history are not affected. Default 30.">
        <Input type="number" min={0} max={3650} value={keepDays} onChange={(e) => setKeepDays(e.target.value)} disabled={!s.can_manage} />
      </FormField>
      {s.can_manage && <div><Button onClick={() => save.mutate()} loading={save.isPending}>Save</Button></div>}
    </div>
  );
}
